/**
 * Cadastro com Google.
 *
 * O Google entrega nome e e-mail, mas não telefone. Quem entra pelo Google sem
 * perfil em `profiles` vai para /register-profile (modo oauth) e só depois
 * a conta é criada, por POST /api/complete-oauth-signup/ com o token.
 *
 * O contexto do cadastro (origin, idioma da LP, país) não sobrevive ao redirect
 * do Google pela URL, então fica no localStorage até o cadastro terminar.
 */
import api from '../config/axios';

const CONTEXT_KEY = 'gio_oauth_signup_ctx';
const CONTEXT_MAX_AGE_MS = 2 * 60 * 60 * 1000;
const ADS_TOKEN_KEYS = {
  google: 'gio_signup_google_ads_token',
  meta: 'gio_signup_meta_token',
};
const OAUTH_PROVIDERS = ['google', 'facebook'];
// O access token do Google dura 1 h; margem para não usar um token quase vencido.
const ADS_TOKEN_MAX_AGE_MS = 50 * 60 * 1000;

/** Escopo do Google Ads pedido no botão do cadastro (o mesmo do FastConnect). */
export const GOOGLE_ADS_SCOPE = 'https://www.googleapis.com/auth/adwords';

export const PROFILE_STATUS = {
  COMPLETE: 'complete',
  MISSING: 'missing',
  UNKNOWN: 'unknown',
};

export function stashOAuthSignupContext(ctx = {}) {
  try {
    const clean = {};
    ['origin', 'preferredLocale', 'countryCode'].forEach((key) => {
      if (ctx[key]) clean[key] = String(ctx[key]);
    });
    if (ctx.withGoogleAds) clean.withGoogleAds = true;
    if (ctx.withMetaAds) clean.withMetaAds = true;
    localStorage.setItem(CONTEXT_KEY, JSON.stringify({ ...clean, savedAt: Date.now() }));
  } catch (_) {
    /* storage bloqueado: o cadastro segue com os padrões */
  }
}

export function readOAuthSignupContext() {
  try {
    const raw = localStorage.getItem(CONTEXT_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) || {};
    if (!parsed.savedAt || Date.now() - parsed.savedAt > CONTEXT_MAX_AGE_MS) {
      localStorage.removeItem(CONTEXT_KEY);
      return {};
    }
    const { savedAt, ...ctx } = parsed;
    return ctx;
  } catch (_) {
    return {};
  }
}

export function clearOAuthSignupContext() {
  try {
    localStorage.removeItem(CONTEXT_KEY);
  } catch (_) {
    /* ignore */
  }
}

/** Provider social do usuário ('google' | 'facebook'), ou null se só e-mail/senha. */
export function oauthProvider(user) {
  if (!user) return null;
  const appMeta = user.app_metadata || {};
  if (OAUTH_PROVIDERS.includes(appMeta.provider)) return appMeta.provider;
  const fromList = (appMeta.providers || []).find((p) => OAUTH_PROVIDERS.includes(p));
  if (fromList) return fromList;
  const identity = (user.identities || []).find((i) => OAUTH_PROVIDERS.includes(i?.provider));
  return identity ? identity.provider : null;
}

/** Usuário do Supabase que entrou (ou já entrou alguma vez) pelo Google ou Facebook. */
export function isOAuthUser(user) {
  return oauthProvider(user) !== null;
}

/** Nome do método para analytics: oauth_google | oauth_facebook. */
export function oauthMethod(provider) {
  return `oauth_${provider || 'google'}`;
}

export function oauthDisplayName(user) {
  const meta = user?.user_metadata || {};
  return String(meta.full_name || meta.name || '').trim();
}

/** State de /register-profile para terminar o cadastro de quem veio do Google/Facebook. */
export function buildOAuthProfileState(user, ctx = {}) {
  return {
    oauth: true,
    oauthProvider: oauthProvider(user) || 'google',
    email: user?.email || '',
    nomeUsuario: oauthDisplayName(user),
    origin: ctx.origin || 'direct',
    preferredLocale: ctx.preferredLocale || undefined,
    countryCode: ctx.countryCode || undefined,
  };
}

/**
 * MISSING só quando a API responde e não há perfil. Erro de rede é UNKNOWN,
 * para nunca mandar alguém com conta pronta de volta para o cadastro.
 */
export async function fetchProfileStatus() {
  try {
    const { data } = await api.get('/api/user/profile/');
    if (data && data.success && !data.profile) return PROFILE_STATUS.MISSING;
    if (data && data.profile) return PROFILE_STATUS.COMPLETE;
    return PROFILE_STATUS.UNKNOWN;
  } catch (_) {
    return PROFILE_STATUS.UNKNOWN;
  }
}

/**
 * Cadastro que pediu as permissões de anúncio no login: o Supabase devolve o
 * token do provider (provider_token) só na sessão da volta. Fica guardado até a
 * conta e o projeto existirem, para conectar sem a segunda tela de OAuth.
 * platform: 'google' (Google Ads) | 'meta' (Meta Ads).
 */
function stashSignupAdsToken(platform, token) {
  if (!token || !ADS_TOKEN_KEYS[platform]) return;
  try {
    localStorage.setItem(ADS_TOKEN_KEYS[platform], JSON.stringify({ token, savedAt: Date.now() }));
  } catch (_) {
    /* sem storage: o onboarding usa o botão normal */
  }
}

function readSignupAdsToken(platform) {
  const key = ADS_TOKEN_KEYS[platform];
  if (!key) return null;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const { token, savedAt } = JSON.parse(raw) || {};
    if (!token || !savedAt || Date.now() - savedAt > ADS_TOKEN_MAX_AGE_MS) {
      localStorage.removeItem(key);
      return null;
    }
    return token;
  } catch (_) {
    return null;
  }
}

function clearSignupAdsToken(platform) {
  try {
    if (ADS_TOKEN_KEYS[platform]) localStorage.removeItem(ADS_TOKEN_KEYS[platform]);
  } catch (_) {
    /* ignore */
  }
}

export const stashSignupGoogleAdsToken = (token) => stashSignupAdsToken('google', token);
export const readSignupGoogleAdsToken = () => readSignupAdsToken('google');
export const clearSignupGoogleAdsToken = () => clearSignupAdsToken('google');

export const stashSignupMetaToken = (token) => stashSignupAdsToken('meta', token);
export const readSignupMetaToken = () => readSignupAdsToken('meta');
export const clearSignupMetaToken = () => clearSignupAdsToken('meta');

export function clearSignupAdsTokens() {
  clearSignupAdsToken('google');
  clearSignupAdsToken('meta');
}

/**
 * Lista as contas do Google Ads com o token do cadastro e deixa tudo como o
 * oauth-callback deixa, para /projects/:id?oauth_success=true&tab=google abrir
 * o seletor. Retorna true se há contas para escolher; false cai no fluxo normal.
 */
export async function prepareGoogleAdsFromSignup(projectId) {
  const token = readSignupGoogleAdsToken();
  clearSignupGoogleAdsToken();
  if (!token || !projectId) return false;
  try {
    const { data } = await api.post('/api/oauth/google-ads/accounts-from-signup-token/', {
      access_token: token,
    });
    const accounts = (data && data.accounts) || [];
    if (accounts.length === 0) return false;
    localStorage.setItem('google_ads_oauth_accounts', JSON.stringify(accounts));
    localStorage.setItem('google_ads_oauth_project_id', String(projectId));
    localStorage.setItem('google_ads_oauth_access_token', token);
    return true;
  } catch (_) {
    return false;
  }
}

/**
 * O botão do Facebook só aparece com REACT_APP_FACEBOOK_LOGIN_ENABLED=true:
 * sem o provider Facebook ativo no Supabase, o clique cai numa página de erro
 * ("provider is not enabled"). Ligar a flag depois de ativar o provider.
 */
export function isFacebookLoginEnabled() {
  return String(process.env.REACT_APP_FACEBOOK_LOGIN_ENABLED || '').toLowerCase() === 'true';
}
